/**
 * Single source of truth for Sample Request pipeline stage labels, chip
 * variants, and allowed transitions.
 *
 * Mirrors the Python constants in
 * `middleware/app/models/sample_requests.py`. Any drift between frontend and
 * backend trips a unit test.
 */

import type { Stage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/** Canonical order. */
export const STAGES: readonly Stage[] = [
  'requested',
  'approved',
  'preparing',
  'shipped',
  'delivered',
  'follow_up',
  'closed_won',
  'closed_no_opportunity',
  'cancelled',
] as const

export const TERMINAL_STAGES: readonly Stage[] = [
  'closed_won',
  'closed_no_opportunity',
  'cancelled',
] as const

/** Non-terminal stages — shown as kanban columns and drag targets. */
export const BOARD_STAGES: readonly Stage[] = [
  'requested',
  'approved',
  'preparing',
  'shipped',
  'delivered',
  'follow_up',
] as const

export const STAGE_LABELS: Record<Stage, string> = {
  requested: 'Requested',
  approved: 'Approved',
  preparing: 'Preparing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  follow_up: 'Follow-up',
  closed_won: 'Closed — Won',
  closed_no_opportunity: 'Closed — No Opportunity',
  cancelled: 'Cancelled',
}

export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  requested: 'Requested',
  approved: 'Approved',
  preparing: 'Preparing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  follow_up: 'Follow-up',
  closed_won: 'Won',
  closed_no_opportunity: 'No Opp.',
  cancelled: 'Cancelled',
}

export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  requested: 'neutral',
  approved: 'info',
  preparing: 'info',
  shipped: 'info',
  delivered: 'success',
  follow_up: 'warning',
  closed_won: 'success',
  closed_no_opportunity: 'neutral',
  cancelled: 'danger',
}

// ─── Transitions (permissive kanban D-5 rule) ─────────────────────
// Mirrors ALLOWED_TRANSITIONS in the Python model:
//   Any non-terminal → any other stage (incl. terminals)
//   Terminals can only undo to `requested`

type Transition = { from: Stage; to: Stage }

const _nonTerminals = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  ..._nonTerminals.flatMap((from) =>
    STAGES.filter((to) => to !== from).map((to) => ({ from, to })),
  ),
  ...TERMINAL_STAGES.map((from) => ({ from, to: 'requested' as Stage })),
]

export function transitionAllowed(from: Stage, to: Stage): boolean {
  if (from === to) return false
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: Stage): Stage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

export function isTerminal(stage: Stage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

// ─── Need-by date — the triage signal (replaces self-graded priority) ──

export interface NeedByUrgency {
  label: string
  variant: ChipVariant
}

/**
 * Urgency derived from the customer's need-by date (date-only, local time).
 * Distinct from the ship-SLA "Overdue to ship" chip — this reflects when the
 * customer needs the sample, not our 2-business-day ship target.
 */
export function needByUrgency(
  iso: string | null | undefined,
  now: Date = new Date(),
): NeedByUrgency | null {
  if (!iso) return null
  const due = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(due.getTime())) return null
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000)
  if (days < 0) return { label: `${-days}d past need-by`, variant: 'danger' }
  if (days === 0) return { label: 'Need by today', variant: 'warning' }
  if (days <= 2) return { label: `Need by in ${days}d`, variant: 'warning' }
  return { label: `Need by in ${days}d`, variant: 'neutral' }
}

// Once a sample has shipped (or later), the customer's need-by date is moot —
// don't show the urgency chip on shipped / delivered / follow-up / terminals.
const _SHIPPED_OR_LATER: readonly Stage[] = [
  'shipped',
  'delivered',
  'follow_up',
  'closed_won',
  'closed_no_opportunity',
  'cancelled',
]

export function isShippedOrLater(stage: Stage): boolean {
  return _SHIPPED_OR_LATER.includes(stage)
}

/** Need-by urgency, but only while the sample still needs to ship. */
export function needByUrgencyForStage(
  iso: string | null | undefined,
  stage: Stage,
  now: Date = new Date(),
): NeedByUrgency | null {
  if (isShippedOrLater(stage)) return null
  return needByUrgency(iso, now)
}

/** Ascending by need-by date; requests with no date sort last. */
export function compareByNeedBy(
  a: { need_by_date: string | null },
  b: { need_by_date: string | null },
): number {
  if (!a.need_by_date && !b.need_by_date) return 0
  if (!a.need_by_date) return 1
  if (!b.need_by_date) return -1
  return a.need_by_date.localeCompare(b.need_by_date)
}
